import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  AccountStatus,
  AppUser,
  AppUserMinimal,
  ProfileHistoryResponse,
  VerificationStatus,
} from './types';

export interface AppUserFilters {
  userId?: string | null;
  externalReference?: string | null;
  loginCountry?: string | null;
  eidProviderType?: string | null;
  emailAddress?: string | null;
  name?: string | null;
  personalIdNumber?: string | null;
  createdAtLte?: string | null;
  createdAtGte?: string | null;
  productInteractions?: string[] | [];
  riskScoreGte?: number | null;
  status?: string | null;
  sourceOfFundsVerificationStatus?: string | null;
  emailAddressVerificationStatus?: string | null;
  phoneNumberVerificationStatus?: string | null;
  screeningPep?: boolean | null;
}

export interface Pagination {
  page: number;
  size: number;
}

interface SearchAggregatedData {
  count: number;
}

export interface SearchAppUsersResponse {
  profiles: AppUser[];
  aggregatedData: SearchAggregatedData;
}

export interface SearchAppUsersV2Response {
  profiles: AppUserMinimal[];
  aggregatedData: SearchAggregatedData;
}

export const searchAppUsers = async ({
  filters,
  pagination,
}: {
  filters: AppUserFilters;
  pagination: Pagination;
}) => {
  return fetchAPI<SearchAppUsersResponse>('v1/app-user:search', {
    method: 'POST',
    body: {
      filters,
      pagination,
    },
  });
};

export const searchAppUsersV2 = async ({
  filters,
  pagination,
}: {
  filters: AppUserFilters;
  pagination: Pagination;
}) => {
  return fetchAPI<SearchAppUsersV2Response>('v2/app-user:search', {
    method: 'POST',
    body: {
      filters,
      pagination,
    },
  });
};

export const searchAppUsersExport = async ({
  filters,
}: {
  filters: AppUserFilters;
}) => {
  const response = await fetchAPI('v1/app-user:export', {
    method: 'POST',
    body: { filters },
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, { type: 'text/csv' });
  return blob;
};

export const updateUser = ({
  userReference,
  riskScoreAdjustment,
  sourceOfFundsVerificationStatus,
  status,
}: {
  userReference: string;
  riskScoreAdjustment?: number | null;
  status?: AccountStatus;
  sourceOfFundsVerificationStatus?: VerificationStatus | null;
}) => {
  let body: any = {
    userType: 'APP',
    status,
  };
  if (riskScoreAdjustment != null) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    body = { ...body, riskScoreAdjustment };
  }
  if (sourceOfFundsVerificationStatus != null) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    body = { ...body, sourceOfFundsVerificationStatus };
  }
  return fetchAPI(`v1/qb-user/${userReference}`, {
    method: 'PUT',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    body,
  });
};

export const deleteUser = (userId: string) =>
  fetchAPI(`v1/app-user/${userId}`, {
    method: 'DELETE',
  });

export const canDeleteUser = (userId: string) =>
  fetchAPI<Record<'canDelete', boolean>>(`v1/app-user/${userId}/can-delete`, {
    method: 'GET',
  });

export const getProfileHistory = async (userReference: string) => {
  const response = await fetchAPI<ProfileHistoryResponse>(
    `v1/qb-user/${userReference}/history`
  );
  response.profileHistory.forEach((history) => {
    history.createdAt = new Date(history.createdAt);
  });
  response.kycHistory.forEach((history) => {
    history.createdAt = new Date(history.createdAt);
  });
  response.onboardingHistory.forEach((history) => {
    history.createdAt = new Date(history.createdAt);
  });
  response.loginHistory.forEach((history) => {
    history.createdAt = new Date(history.createdAt);
  });

  return response;
};

export enum OffboardingPayoutMethod {
  KRAKEN_CRYPTO = 'KRAKEN_CRYPTO',
  BANK_TRANSFER = 'BANK_TRANSFER',
}

export interface OffboardingWallet {
  walletId: string;
  currency: string;
  balance: string;
  value: string;
  valueCurrency: string;
}

export interface OffboardingPendingSend {
  transactionId: string;
  reference: string;
  currency: string;
  amount: string;
  createdAt: string;
}

export interface OffboardingPayout {
  currency: string;
  amount: string;
  method: OffboardingPayoutMethod;
  destination: string;
  payoutReference: string;
}

export interface OffboardingState {
  offboarding: {
    createdAt: string;
    createdBy: string;
    note: string;
    payouts: OffboardingPayout[];
  } | null;
  wallets: OffboardingWallet[];
  pendingSends: OffboardingPendingSend[];
  blockers: string[];
}

export interface OffboardingPayoutInput {
  walletId: string;
  expectedBalance: string;
  method: OffboardingPayoutMethod;
  destination: string;
  payoutReference: string;
}

export const getKycDocumentImages = async (userId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const response = await fetchAPI<any>(
    `v1/qb-user/${userId}/kyc-document-images`
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  return response.kycDocumentImages;
};
export const getKycDocumentImage = async (imageId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const response = await fetchAPI<any>(
    `v1/qb-user/kyc-document-image/${imageId}`
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
  response.savedAt = new Date(response.savedAt);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return response;
};

export const retriggerOnboarding = async (userReference: string) => {
  return fetchAPI(`v1/app-user/${userReference}/onboarding`, {
    method: 'DELETE',
  });
};

export const getOffboarding = async (userId: string) =>
  fetchAPI<OffboardingState>(`v1/qb-user/${userId}/offboarding`);

export const offboardUser = async (
  userId: string,
  payouts: OffboardingPayoutInput[],
  note: string,
  confirmedUnsentSends: string[]
) =>
  fetchAPI<OffboardingState>(`v1/qb-user/${userId}/offboarding`, {
    method: 'POST',
    body: { payouts, note, confirmedUnsentSends },
  });

export const recalculateRiskScores = async () => {
  return await fetchAPI('v1/recalculate-risk-scores', {
    method: 'POST',
  });
};
